import { X } from "lucide-react"

import SpecialOfferImage from "@/assets/img/modal/special_offer_1_image.webp"
import { useCreditsPurchase } from "@/features/advisors"
import { specialOfferPlan, useChat } from "@/features/chat"

import type { AppModalComponentProps } from "../config/modal-registry"

/**
 * Stars pack promo v1 (Figma: Popovers_Stars_Special_Offer, 3153:51926).
 * Charges `specialOfferPlan` via the stored subscription payment method.
 */
export function StarsSpecialOfferV1Modal({ onClose }: AppModalComponentProps) {
  const { userMainSubscriptionId } = useChat()
  const { purchase, isLoading, error } = useCreditsPurchase({ userMainSubscriptionId })

  const handleContinue = () => {
    void purchase(specialOfferPlan, onClose)
  }

  return (
    <div
      data-slot="stars-special-offer-v1-modal"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      {error ? (
        <div className="absolute left-1/2 top-6 z-[10] w-[calc(100%-32px)] max-w-app -translate-x-1/2 rounded-xl bg-peach px-4 py-3 text-center font-body text-sm text-white">
          {error}
        </div>
      ) : null}

      <div className="relative flex w-full flex-col items-center gap-4 rounded-t-3xl bg-dark-surface px-4 pb-4 pt-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          disabled={isLoading}
          className="absolute right-4 top-[15px] z-10 flex size-6 items-center justify-center text-purple-light disabled:opacity-50"
        >
          <X className="size-5" strokeWidth={2} />
        </button>

        <div className="flex w-full flex-col items-center gap-2">
          <img
            src={SpecialOfferImage}
            alt=""
            className="pointer-events-none h-[272px] w-[304px] object-cover"
          />

          <div className="flex h-[30px] items-center rounded-[15px] bg-green/20 px-3 py-2">
            <p className="font-body text-[10px] font-medium uppercase text-green">
              Very special offer!
            </p>
          </div>

          <div className="flex w-full flex-col items-center">
            <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
              Get {specialOfferPlan.creditsCount} stars just for
            </h2>
            <div className="flex items-center gap-2">
              <span className="relative inline-grid">
                <span className="font-display text-2xl font-medium text-purple-light/40">
                  ${specialOfferPlan.unitAmountNoDiscount}
                </span>
                <span
                  aria-hidden
                  className="absolute left-0.5 top-1/2 h-0.5 w-full -translate-y-1/2 bg-peach"
                />
              </span>
              <span className="font-display text-2xl font-medium text-purple-light">
                ${specialOfferPlan.unitAmount}
              </span>
            </div>
          </div>

          <p className="w-full text-center font-body text-base text-purple-light/70">
            Jupiter - the planet of luck and abundance- is shining bright in your chart this week. So
            it&apos;s time to find all the answers at a discounted price!
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-2">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isLoading}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
          >
            {isLoading ? (
              <>
                <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
                Purchasing...
              </>
            ) : (
              "Continue"
            )}
          </button>
          <p className="font-body text-sm text-purple-light/40">One-time payment is non-refundable</p>
        </div>
      </div>
    </div>
  )
}
